import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { loginHref, shouldUseExternalAuthLinks } from '@/utils/authLinks';

const navLinks = [
  { label: 'Philosophy', href: '#philosophy' },
  { label: 'Transformation', href: '#transformation' },
  { label: 'Platform', href: '#platform' },
  { label: 'Stories', href: '#testimonials' },
  { label: 'Essays', href: '#essays' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const authLinkClass = `text-sm font-medium transition-colors duration-300 hover:text-sage-500 ${
    scrolled ? 'text-charcoal-300' : 'text-white/80'
  }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-cream-50/95 backdrop-blur-md shadow-[0_1px_12px_rgba(0,0,0,0.04)]'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={`text-sm md:text-base font-semibold tracking-[0.2em] uppercase transition-colors duration-300 ${
              scrolled ? 'text-charcoal-400' : 'text-white'
            }`}
          >
            LET MONEY SERVE
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                className={`text-sm font-medium transition-colors duration-300 hover:text-sage-400 ${
                  scrolled ? 'text-charcoal-200' : 'text-white/80'
                }`}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* CTA Desktop */}
          <div className="hidden md:flex items-center gap-4">
            {shouldUseExternalAuthLinks ? (
              <a href={loginHref} className={authLinkClass}>
                Sign In
              </a>
            ) : (
              <Link to={loginHref} className={authLinkClass}>
                Sign In
              </Link>
            )}
            <button
              onClick={() => handleNavClick('#cta')}
              className={`text-sm font-medium px-5 py-2 rounded-full transition-all duration-300 whitespace-nowrap ${
                scrolled
                  ? 'bg-sage-500 text-white hover:bg-sage-600'
                  : 'bg-white/15 text-white hover:bg-white/25'
              }`}
            >
              Get Early Access
            </button>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
              scrolled ? 'text-charcoal-400' : 'text-white'
            }`}
          >
            <div className="w-5 h-5 flex flex-col justify-center items-center gap-1">
              <span
                className={`block h-[1.5px] w-5 transition-all duration-300 ${
                  scrolled ? 'bg-charcoal-400' : 'bg-white'
                } ${mobileOpen ? 'rotate-45 translate-y-[2.5px]' : ''}`}
              />
              <span
                className={`block h-[1.5px] w-5 transition-all duration-300 ${
                  scrolled ? 'bg-charcoal-400' : 'bg-white'
                } ${mobileOpen ? '-rotate-45 -translate-y-[2.5px]' : ''}`}
              />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-cream-50/98 backdrop-blur-md border-t border-sand-100 px-6 py-6">
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                className="text-left text-sm font-medium text-charcoal-200 hover:text-sage-500 transition-colors py-2"
              >
                {link.label}
              </button>
            ))}
            <div className="flex flex-col gap-3 mt-2 pt-4 border-t border-sand-100">
              {shouldUseExternalAuthLinks ? (
                <a
                  href={loginHref}
                  className="text-left text-sm font-medium text-charcoal-200 hover:text-sage-500 transition-colors py-2"
                >
                  Sign In
                </a>
              ) : (
                <Link
                  to={loginHref}
                  className="text-left text-sm font-medium text-charcoal-200 hover:text-sage-500 transition-colors py-2"
                >
                  Sign In
                </Link>
              )}
              <button
                onClick={() => handleNavClick('#cta')}
                className="bg-sage-500 text-white text-sm font-medium px-5 py-2.5 rounded-full whitespace-nowrap text-center"
              >
                Get Early Access
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
